import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { CourseModal } from '../components/CourseModal';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ChevronRight, ShieldCheck, HeartPulse, Wind, ArrowRight, Sparkles } from 'lucide-react';

export default function FRTICourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api
      .get('/courses?agency=FRTI')
      .then(({ data }) => setCourses(data || []))
      .catch(() => setCourses([]))
      .finally(() => setLoading(false));
  }, []);

  // Iconography for the 2 standard FRTI courses (matched by slug)
  const ICONS = {
    'adult-child-emergency-care': HeartPulse,
    'oxygen-administration': Wind,
  };

  return (
    <div className="pt-24 pb-20">
      {/* HERO with FRTI logo */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0d2548] to-[#0a1628]" />
          <div className="absolute inset-0 hero-radial" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 relative">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#00b4d8] font-semibold mb-3">
            <Link to="/" className="hover:text-white">Home</Link> <ChevronRight className="inline h-3 w-3 mx-1" /> Courses <ChevronRight className="inline h-3 w-3 mx-1" /> FRTI
          </div>

          <div className="grid lg:grid-cols-[1.2fr,1fr] gap-8 items-center">
            <div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight max-w-3xl">
                First Response Training International
              </h1>
              <p className="mt-5 max-w-2xl text-white/75 text-base sm:text-lg leading-relaxed">
                Emergency-response certifications taught by Arab Diver Tours. Build life-saving confidence with internationally recognised FRTI credentials — designed for divers, dive professionals and anyone who wants to be ready when seconds count.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                <Badge className="bg-white/6 text-white/75 border border-white/12 hover:bg-white/8">
                  <ShieldCheck className="mr-1 h-3 w-3 text-[#00b4d8]" /> Internationally recognised
                </Badge>
                <Badge className="bg-white/6 text-white/75 border border-white/12 hover:bg-white/8">
                  <Sparkles className="mr-1 h-3 w-3 text-[#c8e600]" /> Hands-on practical training
                </Badge>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-3xl bg-white/[0.06] border border-white/12 backdrop-blur p-6 sm:p-8 flex items-center justify-center">
                <img
                  src="/courses/frti/logo.png"
                  alt="First Response Training International"
                  className="w-full max-w-md object-contain"
                  draggable={false}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COURSES — 2 cards (Adult & Child Emergency Care + O2 Administration) */}
      <section className="py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#c8e600] font-semibold mb-2">Available courses</div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Choose your training</h2>
          <p className="mt-3 text-white/65 max-w-2xl">
            Two foundational FRTI programmes — start with whichever fits your goals. Click any card to read the full description, watch the intro video and register your interest.
          </p>

          {loading ? (
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              {[0, 1].map((i) => (
                <div key={i} className="h-72 rounded-3xl bg-white/5 border border-white/10 animate-pulse" />
              ))}
            </div>
          ) : courses.length === 0 ? (
            <Card className="mt-8 rounded-3xl border-white/10 bg-white/[0.04] p-10 text-center text-white/65">
              No FRTI courses yet. Add them from the admin panel.
            </Card>
          ) : (
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              {courses.map((c) => {
                const Icon = ICONS[c.slug] || HeartPulse;
                return (
                  <button
                    key={c.id || c.slug}
                    type="button"
                    onClick={() => setSelected(c)}
                    data-testid={`frti-course-${c.slug}`}
                    className="group text-left rounded-3xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] backdrop-blur p-6 sm:p-8 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00b4d8]"
                  >
                    <div className="flex items-center justify-between">
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#c8e600]/15 border border-[#c8e600]/30 text-[#c8e600]">
                        <Icon className="h-6 w-6" />
                      </span>
                      <ArrowRight className="h-5 w-5 text-white/45 group-hover:text-[#00b4d8] group-hover:translate-x-1 transition" />
                    </div>
                    <div className="mt-5 text-[11px] uppercase tracking-[0.22em] text-[#00b4d8] font-semibold">FRTI</div>
                    <h3 className="mt-1 font-display text-2xl font-semibold leading-snug">{c.name}</h3>
                    {c.description ? (
                      <p className="mt-3 text-white/65 text-sm line-clamp-3 whitespace-pre-line">{c.description}</p>
                    ) : (
                      <p className="mt-3 text-white/45 italic text-sm">
                        Course details coming soon — full description will be available shortly.
                      </p>
                    )}
                    <div className="mt-5 inline-flex items-center text-xs uppercase tracking-wider text-[#00b4d8] font-semibold">
                      View details <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-10">
            <p className="text-xs text-white/45 text-center">
              * OSHA, ILCOR and USCG references are general industry compliance frameworks; programme content is delivered under FRTI standards.
            </p>
          </div>
        </div>
      </section>

      <CourseModal open={!!selected} onOpenChange={(open) => { if (!open) setSelected(null); }} course={selected} />
    </div>
  );
}
